/**
 * A document, as import understands it.
 *
 * Both readers produce this and nothing else: the DOCX reader from OOXML, the PDF
 * reader by reconstructing structure from positioned glyphs. It is deliberately
 * smaller than BroadPaper's template model and deliberately *not* that model, for
 * three reasons.
 *
 * - **It is what the scorer compares.** A synthetic document's ground truth is
 *   written in this shape, and a reader is measured by how close its output comes.
 *   Scoring templates instead would be scoring the builder and the reader together,
 *   and a change that made one better and the other worse would score as nothing.
 * - **It is the seam between two very different problems.** Reading a PDF is
 *   geometry and guesswork; building a template is a deterministic mapping with a
 *   validator at the end. Neither should know the other exists.
 * - **It holds nothing a template could fail to render.** No ids, no expressions,
 *   no tokens: text, structure and a little appearance. The builder is the only
 *   thing that turns it into something the SDK has to accept.
 *
 * Units are points (1/72 inch) for anything measured on a page, because both a PDF
 * and OOXML's half-points and twips convert to them exactly. Colours are `#RRGGBB`.
 */

export type ImportFormat = "docx" | "pdf";

/** One run of text with one appearance. */
export interface ImportedRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** `#RRGGBB`. Absent means the paragraph's own colour. */
  color?: string;
  /** Only `http(s):` and `mailto:` survive into a template. */
  link?: string;
  sizePt?: number;
  /**
   * A value the document computes rather than states: the page number, or the
   * number of pages. `text` holds what the source happened to show — "3" — and the
   * builder replaces it with the live value, so "Page 1 of 3" in an imported footer
   * stays true on a document that runs to five.
   */
  field?: "page" | "pages";
}

export type Alignment = "left" | "center" | "right" | "justify";

export interface ImportedHeading {
  kind: "heading";
  level: 1 | 2 | 3 | 4;
  runs: ImportedRun[];
  align?: Alignment;
}

export interface ImportedParagraph {
  kind: "paragraph";
  runs: ImportedRun[];
  align?: Alignment;
  /**
   * Which of the document's text styles this reads as, when it is not body text.
   * `lead` for an introductory paragraph set larger, `caption` and `small` for the
   * print at the foot of an invoice.
   */
  role?: "lead" | "caption" | "small";
}

export interface ImportedListItem {
  runs: ImportedRun[];
  /** 0 for a top-level item. */
  level: number;
}

export interface ImportedList {
  kind: "list";
  ordered: boolean;
  items: ImportedListItem[];
}

export interface ImportedCell {
  /** The cell's text. Paragraphs inside one cell are joined with a newline. */
  text: string;
  /** The same text with appearance, when the reader has it. */
  runs?: ImportedRun[];
  /** Columns this cell spans. Absent means 1. */
  colSpan?: number;
  align?: Alignment;
  /** `#RRGGBB` shading. */
  background?: string;
}

export interface ImportedTable {
  kind: "table";
  /** Every row, header rows first, each cell once — a spanning cell appears once with `colSpan`. */
  rows: ImportedCell[][];
  /** How many leading rows are headings. 0 when the reader could not tell. */
  headerRows: number;
  /** Relative column widths, in any unit, one per grid column. Absent when unknown. */
  columnWidths?: number[];
  /** True when the table draws cell borders rather than being a layout grid. */
  bordered?: boolean;
}

export interface ImportedImage {
  kind: "image";
  /** A `data:image/…` URI, or null when the image was found but could not be carried over. */
  src: string | null;
  widthPt?: number;
  heightPt?: number;
  alt?: string;
  align?: "left" | "center" | "right";
}

/** Content set side by side: a two-column layout, or a table used for layout. */
export interface ImportedColumns {
  kind: "columns";
  columns: ImportedBlock[][];
  /** Relative widths, one per column. */
  widths?: number[];
}

export interface ImportedDivider {
  kind: "divider";
}

export interface ImportedPageBreak {
  kind: "pageBreak";
}

export type ImportedBlock = ImportedHeading | ImportedParagraph | ImportedList | ImportedTable | ImportedImage | ImportedColumns | ImportedDivider | ImportedPageBreak;

export type BlockKind = ImportedBlock["kind"];

/** The appearance of one of the document's text styles. */
export interface ImportedTextStyle {
  font?: string;
  sizePt?: number;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

export interface ImportedDocument {
  format: ImportFormat;
  /** The document's own title, from its properties, when it has one worth using. */
  title: string | null;
  page: {
    widthPt: number;
    heightPt: number;
    margins: { top: number; right: number; bottom: number; left: number };
  } | null;
  styles: {
    /** The typefaces the document names, before any mapping to fonts a host can embed. */
    headingFont?: string;
    bodyFont?: string;
    /** Accent colours, most prominent first. */
    colours: string[];
    text: Partial<Record<"h1" | "h2" | "h3" | "h4" | "body" | "caption", ImportedTextStyle>>;
  };
  header: ImportedBlock[];
  footer: ImportedBlock[];
  /** Present when the first page has a header or footer of its own. */
  firstPageHeader?: ImportedBlock[];
  firstPageFooter?: ImportedBlock[];
  body: ImportedBlock[];
  /** Pages in the source, when the format says. A DOCX does not, reliably. */
  pages: number | null;
}

/**
 * Something the reader could not carry over, or did differently, said once.
 *
 * `code` is for tests and for grouping; `message` is for the person who chose the
 * file, in the product's voice: what was in the document, and what happened to it.
 */
export interface ImportNotice {
  level: "info" | "warn";
  code:
    | "text-box"
    | "image-skipped"
    | "image-too-large"
    | "unsupported-image"
    | "tracked-changes"
    | "comments"
    | "field-codes"
    | "equation"
    | "chart"
    | "smartart"
    | "section-columns"
    | "nested-table"
    | "wide-table"
    | "scanned-page"
    | "no-text"
    | "encrypted"
    | "truncated"
    | "fonts-substituted"
    | "multiple-sections"
    | "untagged"
    | "other";
  message: string;
  /** How many times it happened, when that is worth saying. */
  count?: number;
}

/** What a reader returns. */
export interface ImportRead {
  document: ImportedDocument;
  notices: ImportNotice[];
}

/** A failure that stops a file being read at all, said for the person who chose it. */
export class ImportError extends Error {
  constructor(
    message: string,
    readonly hint?: string
  ) {
    super(message);
    this.name = "ImportError";
  }
}

/** Caps applied before anything is inflated or parsed. See the DOCX reader for why each exists. */
export interface ImportLimits {
  /** Largest file accepted, in bytes. */
  maxBytes: number;
  /** Total uncompressed bytes a DOCX may declare. */
  maxUncompressedBytes: number;
  maxEntries: number;
  /** Largest single image carried into a template, in bytes, before encoding. */
  maxImageBytes: number;
  /** All images together. */
  maxTotalImageBytes: number;
  /** Pages of a PDF read. */
  maxPages: number;
}

export const IMPORT_LIMITS: ImportLimits = {
  maxBytes: 25 * 1024 * 1024,
  maxUncompressedBytes: 200 * 1024 * 1024,
  maxEntries: 2048,
  maxImageBytes: 1024 * 1024,
  maxTotalImageBytes: 6 * 1024 * 1024,
  maxPages: 40
};

/* --------------------------------------------------------------------------
 * The PDF reader's first half: what pdf.js saw, as plain JSON.
 *
 * Split from reconstruction for a reason the repository has already paid for:
 * pdf.js misbehaves inside the vitest worker (`tests/pdf/extract-text.mjs`), so
 * the half that talks to pdf.js runs in a child process in tests and in the
 * browser in the product, and the half that does the thinking is a pure function
 * over this — testable with a fixture and nothing else.
 *
 * Coordinates are points with the origin at the top-left of the page, y growing
 * downwards, which is the opposite of PDF's own convention and the one every
 * reconstruction rule is easier to write in.
 * ------------------------------------------------------------------------ */

export interface PdfTextItem {
  text: string;
  /** Left edge of the run. */
  x: number;
  /** Top of the run's line box. */
  y: number;
  width: number;
  /** The font size, which is also the run's height for every purpose here. */
  height: number;
  fontName: string;
  /** The family pdf.js resolved, without subset prefixes: "ABCDEF+Inter-Bold" → "Inter". */
  family: string;
  bold: boolean;
  italic: boolean;
  color?: string;
  /** Marked-content id, when the page is tagged. */
  mcid?: number;
}

export interface PdfRect {
  x: number;
  y: number;
  width: number;
  height: number;
  fill?: string;
  stroke?: string;
}

export interface PdfImageBox {
  x: number;
  y: number;
  width: number;
  height: number;
  /** A `data:image/png` crop of the page where the image is drawn, when the host could draw one. */
  src?: string;
}

/** A node of a tagged PDF's structure tree: a role and what it contains. */
export interface PdfStructNode {
  role: string;
  children: Array<PdfStructNode | { mcid: number }>;
  alt?: string;
}

export interface PdfPage {
  number: number;
  width: number;
  height: number;
  items: PdfTextItem[];
  rects: PdfRect[];
  images: PdfImageBox[];
  structure: PdfStructNode | null;
}

/**
 * A surface to draw a PDF page onto, supplied by the host.
 *
 * A browser hands over a `<canvas>`; Node hands over `@napi-rs/canvas`. The reader
 * never imports either — it would tie a package that runs in both places to one of
 * them — and without a factory it simply does not crop images, and says so.
 */
export type CanvasFactory = (width: number, height: number) => {
  /** Passed to pdf.js as `canvasContext`. */
  context: unknown;
  /** The canvas as a `data:image/png;base64,…` URI. */
  toPngDataUrl(): string;
};

export interface PdfContent {
  pages: PdfPage[];
  /** Pages in the file, which may be more than were read. */
  pageCount: number;
  tagged: boolean;
  title: string | null;
  producer: string | null;
}
